import React, { SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import OverFlowIcon from "Components/Common/components/OverFlowIcon";
import { Link } from "react-router-dom";
import { DELETE, MODEL } from "Pages/GMS/ISR/ISRConstants";
import { cloneRecommenderModel, deleteRecommenderModel } from "../ARSServices/ARSServices";
import { toast } from "react-toastify";
import CopyModal from "./CopyModal";
import { CopyIcon, DeleteIcon } from "assests/GmsIcons";
import { APPLET, COPY, CREATED, LAST_UPDATED, STATUS, UPDATED_BY } from "../ARSConstants";
import PromoteDialog from "./PromoteDialog";
import RecomendStatus from "./RecomendStatus";
import { AppContext } from "Context/AppContext";
import { suggestModelName } from "./HelperClass";
import AuthManager from "Common/AuthManager";
import ModelIcon from "./ModelIcon";
import { RecommenderLogsResponse } from "../Types";

type ArsLogsProps = {
    data: RecommenderLogsResponse[];
    setViewLog: React.Dispatch<SetStateAction<boolean>>;
    setLogId: React.Dispatch<SetStateAction<string>>;
    getRecLogs: () => void;
    setLoading: React.Dispatch<SetStateAction<boolean>>;
    promoteModal: boolean;
    generateRecommender: (modelIds: string[]) => void;
    setPromoteModal: React.Dispatch<React.SetStateAction<boolean>>
    selectedItems: RecommenderLogsResponse[]
    setSelectedItems: React.Dispatch<React.SetStateAction<RecommenderLogsResponse[]>>
    paginationParams: TPaginationParams
    setPaginationParams: React.Dispatch<React.SetStateAction<TPaginationParams>>
    createPermission: boolean
    updatePermission: boolean
    deletePermission: boolean
};

const ArsLogsTable = ({ data, getRecLogs, setLoading, setPromoteModal, promoteModal, selectedItems, setSelectedItems, generateRecommender, paginationParams, setPaginationParams, createPermission, updatePermission, deletePermission }: ArsLogsProps) => {
    const [showIndex, setShowIndex] = useState<number | null>(null);
    const [showCopyModal, setShowCopyModal] = useState<boolean>(false);
    const [copiedModel, setCopiedModel] = useState<RecommenderLogsResponse | null>(null);
    let appContext = useContext(AppContext);
    let industryId = appContext?.get_selected_industry()?.id;
    const [promoteLoading, setPromoteLoading] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement>(null);
    const userProfile = AuthManager.getAuthUser()?.profile;

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowIndex(null);
            }
        }
        function handleScroll() {
            if (showIndex !== null) {
                setShowIndex(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showIndex]);

    const handleSort = (column: SortableColumn) => {
        setLoading(true);
        setPaginationParams((prevParams) => {
            const isSameColumn = prevParams.sort_by === column;
            return {
                ...prevParams,
                page: 1,
                sort_by: column,
                sort_by_order: isSameColumn && prevParams.sort_by_order === 'asc' ? 'desc' : 'asc'
            };
        });
    };

    const history = useHistory();

    const closePromoteModal = () => {
        setPromoteModal(false);
        setSelectedItems([])
    };

    const handleSelectItemChange = (item: RecommenderLogsResponse) => {
        setSelectedItems(prevSelectedRows =>
            isItemSelected(item)
                ? prevSelectedRows.filter(response => response.model_id !== item.model_id)
                : [...prevSelectedRows, item]
        );
    };

    const closeModal = () => {
        setShowCopyModal(false);
    };

    const updateCopyModelName = (e: string) => {
        setCopiedModel((prev: any) => {
            const newObj = { ...prev };
            newObj.model_name = e;
            return newObj;
        });
    };

    const copyModel = async () => {
        if (!industryId) return;
        try {
            setSelectedItems([]);
            setShowCopyModal(false);
            setLoading(true);
            const res = await cloneRecommenderModel({
                model_id: copiedModel?.model_id as string,
                model_name: copiedModel?.model_name,
            });
            if (res?.done) {
                getRecLogs();
                toast.success("Copied model succesfully");
            }
            else {
                toast.info(res?.message);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const sortElement = (column: string) => {
        return paginationParams.sort_by === column ? (
            <span className="tw-ml-1"> {paginationParams.sort_by_order === "asc" ? (<i className="fas fa-sort-up" />)
                : (<i className="fas fa-sort-down" />)}
            </span>)
            : (<i className="fas fa-sort" />);
    };

    const tdElement = (rowIndex: number, children: JSX.Element | string | number) => {
        return (<td className={`table-item-style ${rowIndex % 2 === 0 ? "tw-bg-white" : "tw-bg-[#F4F5F7]"}`}>{children}</td>);
    };


    const formCopyName = (name: string) => {
        const existingNames = data.map(x => x?.model_name)
        return suggestModelName(name, existingNames)
    }


    const copyFunction = async (e: any, item: RecommenderLogsResponse) => {
        e.stopPropagation();
        setShowIndex(null);
        const copyName = formCopyName(item?.model_name)
        const newItem = { ...item, model_name: copyName }
        setCopiedModel(newItem);
        setShowCopyModal(true);
    };

    const hasPermission = (
        item: RecommenderLogsResponse,
        action: PermissionAction
    ): PermissionResult => {
        if (!AuthManager.isAuthenticated()) return false;
        if (!userProfile) return false;

        const hasActionPermission = action === 'delete' ? deletePermission : updatePermission;
        if (!hasActionPermission) {
            return `You do not have permission to ${action} model`;
        }

        if (!item?.createdby_userid) return 'User information not available';

        return userProfile.id === item.createdby_userid
            ? true
            : `You can only ${action} models that you have created`;
    };

    const showNoPermissionToast = (message?: string) => {
        toast.error(message ?? "You dont have permission for this operation")
    }

    const deleteFunction = async (e: any, item: RecommenderLogsResponse) => {
        e.stopPropagation();
        setShowIndex(null);
        const permissionStatus = hasPermission(item, 'delete');
        if (permissionStatus !== true && permissionStatus) {
            showNoPermissionToast(permissionStatus);
            return;
        }
        setLoading(true);
        try {
            const res = await deleteRecommenderModel(item?.model_id);
            if (res?.done) {
                setPaginationParams(prevParams => ({
                    ...prevParams,
                    page: 1
                }));
                toast.success("Deleted model succesfully");
            }
            else {
                toast.info(res?.message)
            }
        } catch (error) {
            toast.error(error as string)
            setLoading(false)
        }
        finally {
            setLoading(false)
        }

    };

    const overFlowOptions = (item: RecommenderLogsResponse, icon: JSX.Element, label: string, linkFunction: (e: any, item: RecommenderLogsResponse) => void, enabled: boolean) => {
        return (
            <div className={`tw-py-1 tw-border ${enabled ? '' : 'tw-opacity-50 tw-pointer-events-none'}`} role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                <Link to="#" onClick={(e) => enabled ? linkFunction(e, item) : e.preventDefault()} className={`tw-block tw-px-4 tw-py-1 tw-text-sm tw-text-[#172b4d] ${enabled ? 'hover:tw-bg-[#F0F3FE]' : 'tw-cursor-default'}`} role="menuitem">
                    <div className="tw-flex">
                        <div className="tw-mt-1">{icon}</div>
                        <span className="tw-ml-1">{label}</span>
                    </div>
                </Link>
            </div>
        );
    };

    const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, rowIndex: number) => {
        setShowIndex(rowIndex); event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
    }

    const isItemSelected = (item: RecommenderLogsResponse) => {
        return selectedItems.some(selectedItem => selectedItem.model_id === item.model_id);
    };

    return (
        <div className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2 tw-rounded ">
            <div className="table-container tw-pb-6" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                <table className="tw-bg-[#DFE1E6] tw-w-full tw-table-auto tw-rounded">
                    <thead className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                        <tr>
                            <th className="tw-w-[3%]"></th>
                            <th className="table-header-style" onClick={() => handleSort("model_name")}>{MODEL} {sortElement("model_name")}</th>
                            <th className="table-header-style" onClick={() => handleSort("ars_status")}>{STATUS} {sortElement("ars_status")}</th>
                            <th className="table-header-style">{APPLET}</th>
                            <th className="table-header-style" onClick={() => handleSort("createdOn")}>{CREATED} {sortElement("createdOn")}</th>
                            <th className="table-header-style" onClick={() => handleSort("updatedOn")}>{LAST_UPDATED} {sortElement("updatedOn")}</th>
                            <th className="table-header-style" onClick={() => handleSort("updatedBy")}>{UPDATED_BY} {sortElement("updatedBy")}</th>
                            <th className="table-header-style tw-w-[6%] tw-pe-4"></th>
                        </tr>
                    </thead>
                    <tbody className="tw-bg-white">
                        {data.map((item, rowIndex) => (
                            <tr key={item._id} className="hover:bg-gray-100">
                                {tdElement(
                                    rowIndex, <div><input disabled={['Generating', 'Promoting', 'Promoted'].includes(item?.ars_status)} type="checkbox" checked={isItemSelected(item)} onChange={() => { handleSelectItemChange(item) }} className="tw-bg-transparent tw-text-blue-600" /></div>
                                )}
                                {
                                    tdElement(rowIndex, <div className="tw-text-blue-700 tw-text-sm tw-leading-tight tw-cursor-pointer tw-flex tw-items-center"
                                        onClick={() => { history.push(`ars/model/${item?.model_id}`); }}><span className="tw-mr-2 tw-w-4">{item?.type && <ModelIcon type={item?.type} />}</span>{item?.model_name ?? ""}</div>)
                                }
                                {tdElement(
                                    rowIndex,
                                    <RecomendStatus key={item?._id} status={item?.ars_status ?? ''} />
                                )}
                                {tdElement(
                                    rowIndex,
                                    <>
                                        {/* <div className="tw-text-slate-500 tw-text-xs tw-font-medium tw-font-['Noto Sans'] tw-leading-[14px]">{" "}{item?.applet}</div> */}
                                        <div className="tw-text-blue-950 tw-text-sm tw-leading-tight">{item.applet_name}</div>
                                    </>
                                )}
                                {tdElement(rowIndex, item?.createdOn)}
                                {tdElement(rowIndex, item?.updatedOn)}
                                {tdElement(rowIndex, item?.updatedBy)}
                                {tdElement(
                                    rowIndex,
                                    <>
                                        <div className="tw-cursor-pointer tw-py-2 tw-px-3 tw-rounded-lg hover:tw-bg-shades-of-grey-20"
                                            onClick={(e) => {
                                                if (rowIndex === showIndex) setShowIndex(null);
                                                else { handleOverflowClick(e, rowIndex); }
                                            }}>
                                            <OverFlowIcon />
                                        </div>

                                        {rowIndex === showIndex ? (
                                            <div ref={menuRef}
                                                className="tw-fixed tw-rounded-[4px] tw-z-50  tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}>
                                                <div className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute  tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                    {overFlowOptions(item, <CopyIcon />, COPY, copyFunction, createPermission)}
                                                    {overFlowOptions(item, <DeleteIcon />, DELETE, deleteFunction, deletePermission && userProfile.id === item.createdby_userid)}
                                                </div></div>
                                        ) : null}
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <CopyModal
                showModal={showCopyModal}
                closeModal={closeModal}
                onConfirm={copyModel}
                modelName={copiedModel?.model_name}
                setCopyModelName={updateCopyModelName}
            />
            <PromoteDialog
                showModal={promoteModal}
                closeModal={closePromoteModal}
                loading={promoteLoading} onConfirm={() => {
                    const ids = selectedItems.map(item => item.model_id);
                    generateRecommender(ids);
                }}
                handleSelectItemChange={handleSelectItemChange}
                selectedItems={selectedItems} />
        </div>
    );
};

export default ArsLogsTable;
